import { Banner, Box, Button, Typography } from '@forgeglobal/design-framework';
import {
  Beneficiary,
  BeneficiaryType,
} from '@tcs/client-portal-api/types/Beneficiary';
import BeneficiariesDetail from '@tcs/client-portal-common/components/organisms/BeneficiariesDetail';
import useAccount from '@tcs/client-portal-common/hooks/useAccount';
import useDialogOpen from '@tcs/client-portal-common/hooks/useDialogOpen';
import DetailCard, {
  DetailCardItem,
} from '@tcs/common/components/molecules/DetailCard';
import { Unicode } from '@tcs/common/utils/unicode';
import { isTemporaryAccount } from '@tcs/client-portal-common/utils/accountUtils';
import map from 'lodash/map';
import { useMemo } from 'react';
import { useStyles } from './style';

type BeneficiariesCardProps = {
  accountNumber: string;
  editEnabled?: boolean;
};

const BeneficiariesCard = ({
  accountNumber,
  editEnabled = true,
}: BeneficiariesCardProps) => {
  const classes = useStyles();
  const temporaryAccount = isTemporaryAccount(accountNumber);
  const dialogName = `beneficiary-${accountNumber}`;
  const { setDialogOpen } = useDialogOpen(dialogName);

  const { loading, data: account } = useAccount(accountNumber);

  const actionLabel = useMemo(() => {
    if (!account) {
      return;
    }

    const { primaryBeneficiaries = [], contingentBeneficiaries = [] } = account;

    if (primaryBeneficiaries.length || contingentBeneficiaries.length) {
      return 'Edit';
    }

    return 'Add';
  }, [account]);

  if (loading) {
    return <DetailCard title="Beneficiaries" loading={loading} />;
  }

  if (temporaryAccount) {
    return null;
  }

  if (!account) {
    return null;
  }

  const { primaryBeneficiaries, contingentBeneficiaries } = account;

  const handleOnClick = () => {
    setDialogOpen(true);
  };

  const renderBeneficiaries = (beneficiaries: Beneficiary[]) => {
    if (!beneficiaries?.length) return Unicode.EmDash;
    return map(
      beneficiaries,
      (
        { id, firstName, lastName, beneficiaryType, entityName }: Beneficiary,
        index,
      ) => {
        var beneficiaryName = entityName ?? Unicode.EmDash;

        if (beneficiaryType === BeneficiaryType.Individual) {
          const nameParts = [firstName, lastName].filter(Boolean);
          if (nameParts.length) {
            beneficiaryName = nameParts.join(' ');
          }
        }

        return (
          <Typography
            key={id ?? `beneficiary-${index}`}
            variant="body1"
            className={classes.beneficiaries}>
            {beneficiaryName}
          </Typography>
        );
      },
    );
  };

  const hasBeneficiaries =
    (primaryBeneficiaries && primaryBeneficiaries.length > 0) ||
    (contingentBeneficiaries && contingentBeneficiaries.length > 0);

  return (
    <>
      <DetailCard
        title="Beneficiaries"
        action={
          editEnabled ? (
            <Button
              color="secondary"
              data-test-id="beneficiary-action"
              onClick={handleOnClick}>
              {actionLabel}
            </Button>
          ) : undefined
        }>
        {editEnabled && !hasBeneficiaries && (
          <Box mb={3}>
            <Banner severity="warning" bannerType="inline">
              Your account is missing beneficiary information.
            </Banner>
          </Box>
        )}
        <DetailCardItem label="Primary">
          {renderBeneficiaries(primaryBeneficiaries)}
        </DetailCardItem>
        <DetailCardItem label="Contingent">
          {renderBeneficiaries(contingentBeneficiaries)}
        </DetailCardItem>
      </DetailCard>
      {editEnabled && (
        <BeneficiariesDetail
          contingentBeneficiaries={contingentBeneficiaries}
          primaryBeneficiaries={primaryBeneficiaries}
          setShowDetails={setDialogOpen}
          name={dialogName}
          accountNumber={accountNumber}
        />
      )}
    </>
  );
};

export default BeneficiariesCard;
